import React, { createContext, useCallback, useContext, useEffect, useState } from "react"
import { useMutation, useQuery } from "urql"

import {
  DELETE_BOOKABLE_EVENT_TYPE_MUTATION,
  REMOVE_BOOKABLE_EVENT_TYPE_PHOTO_MUTATION,
  UPDATE_BOOKABLE_EVENT_TYPE_PHOTO_MUTATION,
  UPSERT_BOOKABLE_EVENT_TYPE_MUTATION
} from "./mutations"
import { GET_BOOKABLE_EVENT_TYPES_QUERY } from "./queries"

const initialEventType = {
  name: "",
  slug: "",
  description: "",
  instructions: "",
  fileStackPhoto: null,
  amountCents: 0,
  capacity: null,
  kind: "in_person",
  location: "",
  duration: 30,
  durationUnit: "minute"
}

export const BookableEventTypesContext = createContext()

const onlyNotDeleted = (list) => (list || []).filter((e) => !e.deletedAt && !e.deleted_at)

export const BookableEventTypesProvider = ({ children, bookableEventTypes: bookableEventTypesProp }) => {
  const [showFlyout, setShowFlyout] = useState(false)
  const [bookableEventType, setBookableEventType] = useState({ ...initialEventType })
  const [bookableEventTypes, setBookableEventTypes] = useState(() => onlyNotDeleted(bookableEventTypesProp))
  const [googlePlacesLoaded, setGooglePlacesLoaded] = useState(false)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)

  // Only fetch event types if they are not provided - useful in the AppointmentFlyout
  const [{ data: fetchedEventTypesData, fetching: fetchingEventTypes }] = useQuery({
    query: GET_BOOKABLE_EVENT_TYPES_QUERY,
    pause: !!bookableEventTypesProp
  })

  const [{ fetching: upsertMutationFetching }, upsertBookableEventType] = useMutation(
    UPSERT_BOOKABLE_EVENT_TYPE_MUTATION
  )
  const [{ fetching: photoMutationFetching }, updatePhoto] = useMutation(UPDATE_BOOKABLE_EVENT_TYPE_PHOTO_MUTATION)
  const [{ fetching: deleteMutationFetching }, deleteBookableEventType] = useMutation(
    DELETE_BOOKABLE_EVENT_TYPE_MUTATION
  )
  const [{ fetching: removePhotoMutationFetching }, removePhoto] = useMutation(
    REMOVE_BOOKABLE_EVENT_TYPE_PHOTO_MUTATION
  )

  const fetching =
    upsertMutationFetching ||
    photoMutationFetching ||
    deleteMutationFetching ||
    removePhotoMutationFetching ||
    fetchingEventTypes

  // Set the bookableEventTypes state if it was fetched (only non-deleted)
  useEffect(() => {
    if (fetchedEventTypesData?.bookableEventTypes && !bookableEventTypesProp) {
      setBookableEventTypes(onlyNotDeleted(fetchedEventTypesData.bookableEventTypes))
    }
  }, [fetchedEventTypesData, bookableEventTypesProp])

  // Initialize the Google Places API for the location field
  useEffect(() => {
    async function initPlaces() {
      await window.google.maps.importLibrary("places")
      setGooglePlacesLoaded(true)
    }

    initPlaces()
  }, [])

  const updateBookableEventTypes = (newEventType) => {
    if (!bookableEventTypes) return

    setBookableEventTypes((prevEventTypes) => {
      const newEventTypes = [...prevEventTypes]
      const index = newEventTypes.findIndex((s) => s.id.toString() === newEventType.id.toString())

      if (index !== -1) {
        newEventTypes[index] = newEventType
      } else {
        newEventTypes.push(newEventType)
      }

      return newEventTypes
    })
  }

  const removeBookableEventTypeFromList = (id) => {
    setBookableEventTypes((prevEventTypes) => prevEventTypes.filter((eventType) => eventType.id !== id))
  }

  const resetEventType = useCallback(() => {
    setBookableEventType({ ...initialEventType })
  }, [])

  const editEventType = useCallback((eventType) => {
    setBookableEventType({ ...eventType })
    setShowFlyout(true)
  }, [])

  const addEventType = useCallback(() => {
    resetEventType()
    setShowFlyout(true)
  }, [resetEventType])

  const saveEventType = useCallback(async () => {
    // duration is NOT NULL in DB — never send null/0 (HB 128330830)
    const duration =
      bookableEventType.duration && bookableEventType.duration > 0
        ? bookableEventType.duration
        : bookableEventType.durationUnit === "day"
        ? 1
        : 30

    const result = await upsertBookableEventType({ ...bookableEventType, duration })

    // If success, update the bookableEventTypes state
    if (result?.data?.upsertBookableEventType?.result === "success") {
      updateBookableEventTypes(result.data.upsertBookableEventType.bookableEventType)
    }

    return result
  }, [upsertBookableEventType, bookableEventType])

  const value = {
    bookableEventTypes,
    setBookableEventTypes,
    showFlyout,
    setShowFlyout,
    bookableEventType,
    setBookableEventType,
    editEventType,
    addEventType,
    saveEventType,
    googlePlacesLoaded,
    fetching,
    upsertBookableEventType,
    updatePhoto,
    deleteBookableEventType,
    removePhoto,
    updateBookableEventTypes,
    deleteModalVisible,
    setDeleteModalVisible,
    removeBookableEventTypeFromList
  }

  return <BookableEventTypesContext.Provider value={value}>{children}</BookableEventTypesContext.Provider>
}

export const useBookableEventTypes = () => useContext(BookableEventTypesContext)
